import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, TextField, IconButton, Grid, Radio, RadioGroup, FormControlLabel, Switch, useTheme } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { styled } from '@mui/material/styles';
import { BOTTOM_PANEL_HEIGHT_VAL, BOTTOM_PANEL_VIEWS, INFO_PANEL_VIEWS } from '../../../common/utility/mp_chart_constants';
import { getInfoPanelViewName, getLVOSettingsName } from '../../../common/utility/helperFunc.js';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800, 
  maxHeight: '90vh',
  maxWidth: '90vw',
  bgcolor: 'background.paper',
  borderRadius: '12px',
  boxShadow: 24,
  p: 0,
  overflowY: 'auto',
  overflowX: 'auto',
};

const closeButtonStyle = {
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  color: 'white',
};

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 320,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const VolxSettingsModal = (props) => {
  const theme = useTheme();
  const [open, setOpen] = useState(props.open);

  useEffect(() => {
    setOpen(props.open);
  }, [props.open]);

  const handleClose = () => {
    props.onClose();
    setOpen(false);
  };

  // Import the helper functions to get correct naming
  const getBottomPanelViewName = (plot) => {
    // Use the same naming convention as the chart component expects
    if(plot=="all_volx")
      return "panel1_view_all_volx";
    else if(plot=="all_doi")
      return "panel1_view_oi_change";
    else if(plot=="all_doix")
      return "panel1_view_ntm_oi_change";
    else if(plot=="all_vol")
      return "panel1_view_all_vol";
    else if(plot=="all_dvolx")
      return "panel1_view_all_dvolx";
    else return "unknown_bottom_view";
  };

  const getBottomPanelHeightName = (plot) => {
    // Use the same naming convention as the chart component expects
    if(plot=="all_volx")
      return "panel1_height_all_volx";
    else if(plot=="all_doi")
      return "panel1_height_oi_change";
    else if(plot=="all_doix")
      return "panel1_height_ntm_oi_change";
    else if(plot=="all_vol")
      return "panel1_height_all_vol";
    else if(plot=="all_dvolx")
      return "panel1_height_all_dvolx";
    else return "unknown_bottom_height";
  };

  const getBottomPanelDefaultHeight = (plot) => {
    return BOTTOM_PANEL_HEIGHT_VAL["CANDLE_CHART"]; // Use same default as candle chart
  };

  const currentView = props.userSettings?.data?.[getBottomPanelViewName(props.plot)] || BOTTOM_PANEL_VIEWS.TOP_LAYER;
  const currentHeight = props.userSettings?.data?.[getBottomPanelHeightName(props.plot)] || getBottomPanelDefaultHeight(props.plot);
  const currentInfoPanelView = props.userSettings?.data?.[getInfoPanelViewName(props.plot)] || INFO_PANEL_VIEWS.EXPANDED;
  const currentShowLvo = props.userSettings?.data?.[getLVOSettingsName(props.plot)] || false;

  const [data, setData] = useState({
    panel_view_type: currentView,
    panel_height: currentHeight,
    info_panel_view: currentInfoPanelView,
    show_lvo: currentShowLvo,
  });

  useEffect(() => {
    setData({
      panel_view_type: currentView,
      panel_height: currentHeight,
      info_panel_view: currentInfoPanelView,
      show_lvo: currentShowLvo,
    });
  }, [currentView, currentHeight, currentInfoPanelView, currentShowLvo]);

  const handleBottomPanelViewChange = (event) => {
    setData((prevState) => ({
      ...prevState,
      panel_view_type: event.target.value,
    }));
  };

  const handleHeightChange = (event) => {
    const newValue = event.target.value;
    if (newValue === '' || (newValue >= BOTTOM_PANEL_HEIGHT_VAL["min"] && newValue <= BOTTOM_PANEL_HEIGHT_VAL["max"])) {
      setData((prevState) => ({
        ...prevState,
        panel_height: parseInt(event.target.value) || newValue,
      }));
    }
  };

  const handleInfoPanelViewChange = (event) => {
    setData((prevState) => ({
      ...prevState,
      info_panel_view: event.target.value,
    }));
  };

  const handleShowLvoChange = (event) => {
    setData((prevState) => ({
      ...prevState,
      show_lvo: event.target.checked,
    }));
  };

  const handleSave = () => {
    props.onSave(data);
    setOpen(false);
  };

  return (
    <div>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <IconButton sx={closeButtonStyle} onClick={handleClose}>
            <CancelOutlinedIcon />
          </IconButton>

          <Grid container spacing={2}>
            <Grid item mobile={0} tablet={1} laptop={2}></Grid>

            <Grid item mobile={12} tablet={10} laptop={8} sx={{ minWidth: '760px', ml: 2, mr: 2 }}>
              <Box
                sx={{
                  backgroundColor: 'primaryTheme.shade03',
                  mt: 2,
                  pb: 2,
                  mb: 6,
                  pr: 2,
                  borderRadius: 5,
                  width: '100%',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  flexDirection={'column'}
                  sx={{ mt: 2, mb: 0, ml: 2, pr: 2, width: '100%' }}
                >
                  <Typography sx={{ mr: 1, mt: 2, fontSize: 18, fontWeight: 700, ml: 4 }}>
                    Volx Histogram Panel
                  </Typography>
                  <Typography
                    sx={{
                      mr: 1,
                      mt: 2,
                      mb: 1,
                      fontSize: 14,
                      fontWeight: 500,
                      fontStyle: 'italic',
                      ml: 6,
                    }}
                  >
                    Container specific temporary settings only.
                    <br />
                    For changing and saving the default settings, use the Settings section in bottom left corner.
                  </Typography>

                  {/* Top Left Info Panel View */}
                  <Box display="flex" justifyContent="flex-start" sx={{ mb: 0, ml: 4, width: '100%' }}>
                    <Box sx={{ width: '272px', display: 'flex' }}>
                      <Typography sx={{ mr: 1, mt: 2, fontSize: 14 }}>Top Left Info Panel View</Typography>
                      <HtmlTooltip
                        title={
                          <React.Fragment>
                            <Typography color="inherit">Info Panel Visibility (Top left panel)</Typography>
                            <li>Expanded: Default Info panel will be expanded view.</li>
                            <li>Collapsed: Default Info panel will be collapsed view.</li>
                          </React.Fragment>
                        }
                      >
                        <InfoIcon sx={{ marginTop: '14px' }}></InfoIcon>
                      </HtmlTooltip>
                    </Box>

                    <RadioGroup
                      row
                      sx={{ ml: 1, mt: 1 }}
                      size="small"
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                    >
                      <FormControlLabel
                        value={INFO_PANEL_VIEWS.EXPANDED}
                        control={
                          <Radio
                            checked={data.info_panel_view === INFO_PANEL_VIEWS.EXPANDED}
                            onChange={handleInfoPanelViewChange}
                            size="small"
                          />
                        }
                        label={<Typography sx={{ fontSize: 14 }}>Expanded</Typography>}
                      />
                      <FormControlLabel
                        value={INFO_PANEL_VIEWS.COLLAPSED}
                        control={
                          <Radio
                            checked={data.info_panel_view === INFO_PANEL_VIEWS.COLLAPSED}
                            onChange={handleInfoPanelViewChange}
                            size="small"
                          />
                        }
                        label={<Typography sx={{ fontSize: 14 }}>Collapsed</Typography>}
                      />
                    </RadioGroup>
                  </Box>

                  {/* Bottom Panel Visibility */}
                  <Box display="flex" justifyContent="flex-start" sx={{ mb: 0, ml: 4, width: '100%' }}>
                    <Box sx={{ width: '272px', display: 'flex' }}>
                      <Typography sx={{ mr: 1, mt: 2, fontSize: 14 }}>Bottom Panel Visibility</Typography>
                      <HtmlTooltip
                        title={
                          <React.Fragment>
                            <Typography color="inherit">Bottom Panel Visibility</Typography>
                            <li>Top Layer: Show bottom panel at top of main panel.</li>
                            <li>Same Container: Main panel overlap with bottom panel.</li>
                            <li>Hidden: Hide bottom panel.</li>
                          </React.Fragment>
                        }
                      >
                        <InfoIcon sx={{ marginTop: '14px' }}></InfoIcon>
                      </HtmlTooltip>
                    </Box>

                    <RadioGroup
                      row
                      sx={{ ml: 1, mt: 1 }}
                      size="small"
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                    >
                      <FormControlLabel
                        value={BOTTOM_PANEL_VIEWS.TOP_LAYER}
                        control={
                          <Radio
                            checked={data.panel_view_type === BOTTOM_PANEL_VIEWS.TOP_LAYER}
                            onChange={handleBottomPanelViewChange}
                            size="small"
                          />
                        }
                        label={<Typography sx={{ fontSize: 14 }}>Top Layer</Typography>}
                      />
                      <FormControlLabel
                        value={BOTTOM_PANEL_VIEWS.SAME_CONTAINER}
                        control={
                          <Radio
                            checked={data.panel_view_type === BOTTOM_PANEL_VIEWS.SAME_CONTAINER}
                            onChange={handleBottomPanelViewChange}
                            size="small"
                          />
                        }
                        label={<Typography sx={{ fontSize: 14 }}>Same Container</Typography>}
                      />
                      <FormControlLabel
                        value={BOTTOM_PANEL_VIEWS.HIDDEN}
                        control={
                          <Radio
                            checked={data.panel_view_type === BOTTOM_PANEL_VIEWS.HIDDEN}
                            onChange={handleBottomPanelViewChange}
                            size="small"
                          />
                        }
                        label={<Typography sx={{ fontSize: 14 }}>Hidden</Typography>}
                      />
                    </RadioGroup>
                  </Box>

                  {/* Bottom Panel Height */}
                  <Box display="flex" justifyContent="flex-start" sx={{ mt: 2, mb: 0, ml: 4, width: '100%' }}>
                    <Box sx={{ width: '272px', display: 'flex' }}>
                      <Typography sx={{ mr: 1, mt: 2, fontSize: 14 }}>Bottom Panel Height in %</Typography>
                      <HtmlTooltip
                        title={
                          <React.Fragment>
                            <Typography color="inherit">
                              Bottom Panel height wrt main panel height in percentage [5,60]
                            </Typography>
                          </React.Fragment>
                        }
                      >
                        <InfoIcon sx={{ marginTop: '12px' }}></InfoIcon>
                      </HtmlTooltip>
                    </Box>
                    <TextField
                      size="small"
                      label=""
                      type="number"
                      value={data.panel_height}
                      onChange={handleHeightChange}
                      sx={{ height: '25px', ml: 1, mt: 1, padding: '1px' }}
                      inputProps={{
                        min: BOTTOM_PANEL_HEIGHT_VAL['min'],
                        max: BOTTOM_PANEL_HEIGHT_VAL['max'],
                      }}
                    />
                  </Box>

                  {/* Show Intra Volx lines only */}
                  <Box display="flex" justifyContent="flex-start" sx={{ mt: 2, mb: 0, ml: 4, width: '100%' }}>
                    <Box sx={{ width: '272px', display: 'flex' }}>
                      <Typography sx={{ mr: 1, mt: 1, fontSize: 14 }}>Show Intra Volx lines only</Typography>
                      <HtmlTooltip
                        title={
                          <React.Fragment>
                            <Typography color="inherit">
                              If this setting is set and saved, chart will only show active Intra day Volx lines.<br/><br/>
                              By default the Volx Lines for all dates are shown if this setting is Disabled.<br/><br/>
                              This is helpful, when the previous day was expiry and the Volx volumes range was so large that next day Intra volx lines are barely visible.
                            </Typography>
                          </React.Fragment>
                        }
                      >
                        <InfoIcon sx={{ marginTop: '6px' }}></InfoIcon>
                      </HtmlTooltip>
                    </Box>

                    <Switch
                      checked={data.show_lvo}
                      color="secondary"
                      onChange={handleShowLvoChange}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Buttons */}
              <Box sx={{ mt: 4, mb: 0, display: 'flex', justifyContent: 'end' }}>
                <Button variant="contained" onClick={handleClose} sx={{ mr: 2, bgcolor: '#ffffff', color: 'black' }}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} sx={{ mr: 2 }}>
                  Apply
                </Button>
              </Box>
            </Grid>

            <Grid item mobile={0} tablet={1} laptop={2}></Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
};

export default VolxSettingsModal;

